import { useState, type ReactElement } from 'react';

import { illustrations } from '../../assets/illustrations';
import { SensitivityChip } from '../../atoms/SensitivityChip';
import { TagChip } from '../../atoms/TagChip';
import { useHoverWithDebounce, usePopoverPlacement } from '../../hooks';
import { MaskIcon } from '../../lib/mask-icon';
import { DRILL_PRIMARY_BUTTON_CLASS } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';
import { IconCount } from '../../molecules/IconCount';
import {
  StackedSegmentBar,
  type StackedSegmentBarSegment,
} from '../../molecules/StackedSegmentBar';
import { UserTypeBadge } from '../../molecules/UserTypeBadge';

/**
 * Single sensitivity-tier (or regulation-bucket) segment carried on a body row.
 * The wrapper resolves the `color` from `SENSITIVITY_TIERS` in
 * `@dawn/feature-config/widgets`; the body never reads the registry. Per
 * design Decision Log #8 the molecule consumes the wrapper-shaped
 * `{ id, percent, color }` segment shape — this prop type carries the API's
 * `{ sensitivityId, label, count, color }` shape and the body computes
 * `percent = count / totalCount * 100` per row.
 */
export interface TopIdentitiesByFileAccessSensitivitySegment {
  /** Stable sensitivity id from the API (drives StackedSegmentBar key + data-testid). */
  sensitivityId: string;
  /** Display label for screen-reader announcement. */
  label: string;
  /** Count of files at this sensitivity tier accessed by the actor. */
  count: number;
  /** Solid hex color resolved by the wrapper from SENSITIVITY_TIERS palette. */
  color: string;
}

export interface TopIdentitiesByFileAccessTopic {
  /** Stable id used as the React key on the rendered TagChip. */
  id: string;
  /** Display label rendered inside the TagChip. */
  label: string;
}

export interface TopIdentitiesByFileAccessRow {
  /** Stable row id (drives React key + `data-testid="top-identities-row-{id}"`). */
  id: string;
  /** Actor display label (email or service-account handle). */
  actor: string;
  /** API enum from `TopIdentityUserType`: 'HI' | 'NHI'. */
  userType: 'HI' | 'NHI';
  /** Drives the optional adjacent privileged tile in `UserTypeBadge`. Omitted when the source has no privilege signal: no tile, no aria suffix. */
  isPrivileged?: boolean;
  /** Total files this actor accessed in the time window. */
  filesAccessed: number;
  /** N-segment palette for the inline 7px sensitivity bar; widths derive from `count`. */
  sensitivityBreakdown: ReadonlyArray<TopIdentitiesByFileAccessSensitivitySegment>;
  /** Document-topic chips (TagChip sm subtle). */
  documentTopics: ReadonlyArray<TopIdentitiesByFileAccessTopic>;
  /** Regulation-topic chips (TagChip sm subtle). */
  regulationTopics: ReadonlyArray<TopIdentitiesByFileAccessTopic>;
  /** Forward-compat — preserved on the prop type but renders no visual today. */
  isHighlighted?: boolean;
}

/**
 * Display labels for the 5 column headers. Defaults to English; consumers in
 * non-English locales can override per cell.
 */
export interface TopIdentitiesByFileAccessHeaderLabels {
  actor: string;
  userType: string;
  filesAccessed: string;
  documentTopics: string;
  regulationTopics: string;
}

export interface TopIdentitiesByFileAccessBodyProps {
  /** Pre-sorted (by `filesAccessed` desc) rows from the API. May be empty. */
  rows: ReadonlyArray<TopIdentitiesByFileAccessRow>;
  /**
   * Optional i18n hook for the per-row `UserTypeBadge` aria-label. Defaults to
   * an English string ("Human identity" / "Non-human identity", suffixed with
   * ", privileged" when applicable). Consumers can supply a localized builder
   * to keep the organism consumer-agnostic.
   */
  getUserTypeBadgeAriaLabel?: (userType: 'HI' | 'NHI', isPrivileged: boolean | undefined) => string;
  /** i18n column-header labels. Defaults to English (matching Figma `5:23634`). */
  headerLabels?: TopIdentitiesByFileAccessHeaderLabels;
  /** i18n empty-state message rendered when `rows.length === 0`. Defaults to English. */
  emptyMessage?: string;
  /**
   * i18n hook for the per-row sensitivity-bar aria-label. Receives the row's
   * `actor` and returns the announcement string. Defaults to English.
   */
  getSensitivityBarAriaLabel?: (actor: string) => string;
  /**
   * Row click (TDEV-1116). When supplied, each row takes a mouse click (the
   * user-type badge included: it is that identity) and its actor is a
   * `<button>` for the keyboard; when omitted, rows are plain `<div>`s.
   */
  onRowClick?: (row: TopIdentitiesByFileAccessRow) => void;
  className?: string;
}

const DEFAULT_USER_TYPE_BADGE_ARIA_LABEL = (
  userType: 'HI' | 'NHI',
  isPrivileged: boolean | undefined
): string =>
  `${userType === 'HI' ? 'Human' : 'Non-human'} identity${isPrivileged === true ? ', privileged' : ''}`;

const DEFAULT_HEADER_LABELS: TopIdentitiesByFileAccessHeaderLabels = {
  actor: 'Actor',
  userType: 'User Type',
  filesAccessed: 'File Accessed',
  documentTopics: 'Document Topics',
  regulationTopics: 'Regulation Topics',
};

const DEFAULT_EMPTY_MESSAGE = 'No identities found in the selected time window.';

const DEFAULT_SENSITIVITY_BAR_ARIA_LABEL = (actor: string): string =>
  `Sensitivity breakdown for ${actor}`;

/**
 * Body grid template — 5 column tracks, 155 / 73 / 205 / 350 / 325 = 1108px.
 * Figma `5:23634` has 125 / 73 / 205 / 365 / 340; the actor track took 30px
 * from the two topic tracks because an email is longer than a display name
 * (TDEV-1117). Header and rows share the grid via `display: contents` so cell
 * `<div>`s flow directly into the parent grid, mirroring the
 * `TopDataAccessedBody` precedent.
 *
 * The two topic tracks are `minmax(0, <px>)`: at 1108px or wider every track
 * sits at its full width, and below that only the topic columns give way
 * (equally), down to ~278px each in the 990px body at a 1280px viewport with
 * the sidebar open. Their chips already clip at the cell edge. The actor track
 * stays 155px; a long email truncates inside it.
 *
 * `justify-between` distributes any leftover horizontal space (parent width
 * minus the 1108px of tracks) evenly between the 5 columns, matching Figma
 * `5:23634`, which renders the body at 1301px (1440px viewport, sidebar
 * collapsed) with ~48px gaps between columns. At 1150px (sidebar open) the
 * gaps shrink to ~10px each; first/last cells stay flush with the parent
 * edges. This is the canonical "Figma equal-gap distribution" pattern
 * codified by TDEV-168 DEBUG.
 *
 * `items-center` vertically centers each cell's content within the row track
 * so every cell — including the File Accessed cell's inline IconCount +
 * StackedSegmentBar pair (Figma `5:23666`) — vertically aligns with the
 * single-line Actor / User Type / Topic chip cells. Mirrors the
 * `TopDataAccessedBody` precedent.
 */
const BODY_GRID =
  'grid w-full items-center justify-between grid-cols-[155px_73px_205px_minmax(0,350px)_minmax(0,325px)]';

// Shared column template used for both the header grid and the data sub-grid
// so Tailwind generates both classes (they're identical here but referenced
// separately to make the two-grid pattern explicit).
const DATA_GRID_COLS = 'grid-cols-[155px_73px_205px_minmax(0,350px)_minmax(0,325px)]';

// Bracketed font-size literal + colour utility on the same element is safe
// here: the bracketed literal sits in tailwind-merge's font-size group and no
// longer collides with the `text-grey-600` colour utility (the collision is
// specific to the custom `text-p*` tokens — see TagChip pattern).
const HEADER_CELL = 'px-2 py-2 text-[12px] font-normal leading-[1.4] text-grey-600';

// Row cells carry no per-row borders per Figma `5:23634` — only the
// header→body divider renders a 1px `grey-200` line.
// Fixed h-[28px] per Figma `5:23634` row spec; flex items-center vertically
// centers content within the 28px track.
const ROW_CELL = 'h-[28px] px-2 flex items-center';

/**
 * `TopIdentitiesByFileAccessBody` — stateless body of the Top Identities by
 * File Access dashboard widget per Figma `5:23634`. Composes Phase 1 frozen
 * molecules (`UserTypeBadge`, `StackedSegmentBar size='sm'`) plus existing
 * primitives (`TagChip`, `IconCount`, the `MaskIcon` helper).
 *
 * Owns no state. Every re-render is a pure function of `props.rows` per design
 * Decision Log #5; the wrapper handles narrowing, palette lookup, and reshape.
 *
 * Empty rows render an inline "No identities found in the selected time
 * window." helper with `data-testid="top-identities-empty-helper"`. Per design
 * Decision Log #4.
 *
 * Rows are `<div>`s. With `onRowClick` a row takes a mouse click and its actor
 * becomes a `<button>`. The `data-testid="top-identities-row-{id}"` is the
 * contract surface for wrapper/integration tests.
 *
 * `@dawn/ui` agnosticism preserved — this organism imports no values from
 * `@dawn/feature-config`.
 */
export function TopIdentitiesByFileAccessBody({
  rows,
  getUserTypeBadgeAriaLabel = DEFAULT_USER_TYPE_BADGE_ARIA_LABEL,
  headerLabels = DEFAULT_HEADER_LABELS,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  getSensitivityBarAriaLabel = DEFAULT_SENSITIVITY_BAR_ARIA_LABEL,
  onRowClick,
  className,
}: TopIdentitiesByFileAccessBodyProps): ReactElement {
  return (
    <div
      data-testid="top-identities-by-file-access-body"
      className={cn('flex w-full flex-col gap-2 font-outfit text-dawn-black', className)}
    >
      <div data-testid="top-identities-grid" className={BODY_GRID}>
        <HeaderRow headerLabels={headerLabels} />
        {/*
         * Data sub-grid: same column template as the header grid so column
         * edges align across both grids (both are w-full with justify-between).
         * mt-4 (16px) = Figma line-to-first-row spec. gap-y-1 (4px) = Figma
         * row gap. Isolated here so gap-y never bleeds into header spacing.
         */}
        <div
          className={cn(
            'col-span-5 mt-4 grid w-full items-center justify-between gap-y-1',
            DATA_GRID_COLS
          )}
        >
          {rows.length === 0 ? (
            <p
              data-testid="top-identities-empty-helper"
              className="col-span-5 px-2 py-4 text-p2 text-grey-500"
            >
              {emptyMessage}
            </p>
          ) : (
            rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                getUserTypeBadgeAriaLabel={getUserTypeBadgeAriaLabel}
                getSensitivityBarAriaLabel={getSensitivityBarAriaLabel}
                filesAccessedLabel={headerLabels.filesAccessed}
                onRowClick={onRowClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

TopIdentitiesByFileAccessBody.displayName = 'TopIdentitiesByFileAccessBody';

function HeaderRow({
  headerLabels,
}: {
  headerLabels: TopIdentitiesByFileAccessHeaderLabels;
}): ReactElement {
  return (
    <div data-testid="top-identities-header-row" className="contents">
      <div className={HEADER_CELL}>{headerLabels.actor}</div>
      <div className={HEADER_CELL}>{headerLabels.userType}</div>
      <div className={HEADER_CELL}>{headerLabels.filesAccessed}</div>
      <div className={HEADER_CELL}>{headerLabels.documentTopics}</div>
      <div className={HEADER_CELL}>{headerLabels.regulationTopics}</div>
      {/*
       * Full-width header→body divider per Figma `5:23634`: continuous 1px
       * `grey-200` line spanning all 5 grid tracks. Matches the
       * `TopDataAccessedBody` precedent (col-span equals the column count).
       */}
      <div
        data-testid="top-identities-header-divider"
        className="col-span-5 border-b border-grey-200"
      />
    </div>
  );
}

function Row({
  row,
  getUserTypeBadgeAriaLabel,
  getSensitivityBarAriaLabel,
  filesAccessedLabel,
  onRowClick,
}: {
  row: TopIdentitiesByFileAccessRow;
  getUserTypeBadgeAriaLabel: (userType: 'HI' | 'NHI', isPrivileged: boolean | undefined) => string;
  getSensitivityBarAriaLabel: (actor: string) => string;
  filesAccessedLabel: string;
  onRowClick?: (row: TopIdentitiesByFileAccessRow) => void;
}): ReactElement {
  const totalSensitivityCount = row.sensitivityBreakdown.reduce((acc, seg) => acc + seg.count, 0);
  // Compute percent as count / totalCount * 100. Guarded by Number.isFinite
  // so a hypothetical NaN/Infinity never bubbles into the StackedSegmentBar
  // width style. When `totalSensitivityCount === 0` (no files), we render no
  // segments — StackedSegmentBar renders an empty wrapper at h-[7px]
  // gracefully without throwing. Per the locked plan math.
  const segments: StackedSegmentBarSegment[] =
    totalSensitivityCount > 0
      ? row.sensitivityBreakdown.map((seg) => {
          const percent = (seg.count / totalSensitivityCount) * 100;
          return {
            id: seg.sensitivityId,
            percent: Number.isFinite(percent) ? percent : 0,
            color: seg.color,
          };
        })
      : [];

  const ariaLabel = getUserTypeBadgeAriaLabel(row.userType, row.isPrivileged);
  const hover = useHoverWithDebounce();
  const [filesCell, setFilesCell] = useState<HTMLDivElement | null>(null);
  // The card lists what the bar draws: a zero-count tier has no segment.
  const breakdown = row.sensitivityBreakdown.filter((seg) => seg.count > 0);

  return (
    <div
      data-testid={`top-identities-row-${row.id}`}
      className={cn('contents', onRowClick && 'group/drill cursor-pointer')}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {/*
       * Actor cell — the identity's email, or its id (TDEV-1117), per Figma
       * `5:23636` typography: 10px, leading-[1.2]. One truncated line with the
       * full value on hover: an address wrapping past the 28px row spills into
       * the next track. Never capitalized; an address keeps its case.
       */}
      <div className={cn(ROW_CELL, 'text-[10px] leading-[1.2] text-dawn-black')} title={row.actor}>
        {onRowClick ? (
          // Activation bubbles to the row's onClick.
          <button type="button" className={cn(DRILL_PRIMARY_BUTTON_CLASS, 'max-w-full')}>
            <span className="block truncate">{row.actor}</span>
          </button>
        ) : (
          <span className="truncate">{row.actor}</span>
        )}
      </div>
      <div className={ROW_CELL}>
        <UserTypeBadge type={row.userType} isPrivileged={row.isPrivileged} aria-label={ariaLabel} />
      </div>
      {/* The whole cell, not only the 7px bar, opens the breakdown card (TDEV-1133). */}
      <div
        data-testid={`top-identities-files-cell-${row.id}`}
        ref={setFilesCell}
        className={cn(ROW_CELL, 'flex items-center gap-3')}
        onMouseEnter={hover.onMouseEnter}
        onMouseLeave={hover.onMouseLeave}
      >
        <IconCount
          icon={<MaskIcon src={illustrations.fileOutline} size={16} />}
          count={row.filesAccessed}
          size={16}
        />
        <StackedSegmentBar
          size="sm"
          segments={segments}
          aria-label={getSensitivityBarAriaLabel(row.actor)}
          className="w-[140px]"
        />
        {hover.isHovered && breakdown.length > 0 && filesCell !== null ? (
          <SensitivityBreakdownCard
            anchor={filesCell}
            title={filesAccessedLabel}
            total={row.filesAccessed}
            breakdown={breakdown}
          />
        ) : null}
      </div>
      {/*
       * Document Topics + Regulation Topics cells — single-line chip row per
       * Figma `5:23634`, which fits 5 doc chips in the 380px column inline.
       * `overflow-hidden` is the graceful-degradation clip if a chip combo
       * collectively exceeds the cell width (e.g. 5 reg chips in the 280px
       * column with long labels — the right-most chip clips at the column
       * edge rather than wrapping to a second line).
       */}
      <div className={cn(ROW_CELL, 'flex items-center gap-1 overflow-hidden whitespace-nowrap')}>
        {row.documentTopics.map((t) => (
          <TagChip key={t.id} state="subtle" size="sm">
            {t.label}
          </TagChip>
        ))}
      </div>
      <div className={cn(ROW_CELL, 'flex items-center gap-1 overflow-hidden whitespace-nowrap')}>
        {row.regulationTopics.map((t) => (
          <TagChip key={t.id} state="subtle" size="sm">
            {t.label}
          </TagChip>
        ))}
      </div>
    </div>
  );
}

/**
 * Hover card for the File Accessed cell (TDEV-1133): the row's total, then one
 * row per tier — the tinted chip names the bar segment of the same colour, so
 * colour is never the only carrier. Same chip + file-count rows as the
 * PipePopover sensitivity block. Rows render in the order given; the wrapper
 * sorts them by tier. Placed below the cell, flipped above when it would leave
 * the widget card (the bottom rows).
 */
function SensitivityBreakdownCard({
  anchor,
  title,
  total,
  breakdown,
}: {
  anchor: HTMLElement;
  title: string;
  total: number;
  breakdown: ReadonlyArray<TopIdentitiesByFileAccessSensitivitySegment>;
}): ReactElement {
  const { ref, style } = usePopoverPlacement(anchor);
  return (
    <div
      ref={ref}
      style={style}
      data-testid="top-identities-sensitivity-popover"
      className="flex min-w-[217px] flex-col gap-3 rounded-lg border border-grey-200 bg-white p-4 shadow-lg"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-[13px] font-medium leading-[1.3]">{title}</span>
        <span className="text-[13px] font-medium leading-[1.3] tabular-nums">
          {total.toLocaleString('en-US')}
        </span>
      </div>
      <div className="h-px w-full bg-grey-200" aria-hidden="true" />
      {breakdown.map((seg) => (
        <div
          key={seg.sensitivityId}
          data-testid={`top-identities-sensitivity-popover-row-${seg.sensitivityId}`}
          className="flex items-center justify-between gap-4"
        >
          <SensitivityChip level={seg.sensitivityId} label={seg.label} />
          <IconCount
            icon={<MaskIcon src={illustrations.fileOutline} size={14} />}
            count={seg.count}
          />
        </div>
      ))}
    </div>
  );
}
